// Геометрия для "курсора" маршрута - точки, магнитом примагниченной к линии
// и синхронной между картой и графиком высот. Без зависимости от карты,
// чтобы математику можно было тестировать отдельно.

// Тот же формат, что PointLLE в routeLineStyle.ts: [lat, lng, elevation | null].
export type CursorRoutePoint = [number, number, number | null];

export interface CursorProjection {
  km: number;
  lat: number;
  lng: number;
  elevationM: number | null;
  // Расстояние в метрах от точки клика до примагниченной точки - используется как допуск попадания.
  distanceToLineM: number;
}

export interface CursorPosition {
  lat: number;
  lng: number;
  elevationM: number | null;
}

const EARTH_RADIUS_M = 6371000;
const DEG = Math.PI / 180;

const haversineM = (lat1: number, lng1: number, lat2: number, lng2: number): number => {
  const dLat = (lat2 - lat1) * DEG;
  const dLng = (lng2 - lng1) * DEG;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1 * DEG) * Math.cos(lat2 * DEG) * Math.sin(dLng / 2) ** 2;
  return EARTH_RADIUS_M * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

const lerp = (a: number, b: number, t: number) => a + (b - a) * t;

const lerpElevation = (
  a: number | null | undefined,
  b: number | null | undefined,
  t: number
): number | null => {
  if (typeof a === "number" && typeof b === "number") return lerp(a, b, t);
  if (typeof a === "number") return a;
  if (typeof b === "number") return b;
  return null;
};

/** Накопленное расстояние (км) в каждой точке маршрута. */
export const buildCumulativeKm = (points: CursorRoutePoint[]): number[] => {
  const out: number[] = [0];
  for (let i = 1; i < points.length; i++) {
    out.push(out[i - 1] + haversineM(points[i - 1][0], points[i - 1][1], points[i][0], points[i][1]) / 1000);
  }
  return out;
};

/**
 * Проецирует точку на ближайшую точку линии маршрута.
 *
 * Считает в локальной равнопромежуточной проекции вокруг широты сегмента -
 * точности с запасом хватает на длину типичного сегмента похода.
 */
export const nearestPointOnRoute = (
  points: CursorRoutePoint[],
  lat: number,
  lng: number,
  cumulativeKm?: number[]
): CursorProjection | null => {
  if (points.length < 2) return null;
  const cumKm = cumulativeKm ?? buildCumulativeKm(points);

  let best: CursorProjection | null = null;

  for (let i = 0; i < points.length - 1; i++) {
    const [aLat, aLng, aEl] = points[i];
    const [bLat, bLng, bEl] = points[i + 1];
    const cosLat = Math.cos(aLat * DEG);
    // Локальные метры относительно начала сегмента.
    const ax = 0;
    const ay = 0;
    const bx = (bLng - aLng) * DEG * EARTH_RADIUS_M * cosLat;
    const by = (bLat - aLat) * DEG * EARTH_RADIUS_M;
    const px = (lng - aLng) * DEG * EARTH_RADIUS_M * cosLat;
    const py = (lat - aLat) * DEG * EARTH_RADIUS_M;

    const segLenSq = (bx - ax) ** 2 + (by - ay) ** 2;
    const t = segLenSq === 0 ? 0 : Math.max(0, Math.min(1, ((px - ax) * (bx - ax) + (py - ay) * (by - ay)) / segLenSq));

    const cx = ax + (bx - ax) * t;
    const cy = ay + (by - ay) * t;
    const distM = Math.hypot(px - cx, py - cy);

    if (best === null || distM < best.distanceToLineM) {
      const segKm = cumKm[i + 1] - cumKm[i];
      best = {
        km: cumKm[i] + segKm * t,
        lat: lerp(aLat, bLat, t),
        lng: lerp(aLng, bLng, t),
        elevationM: lerpElevation(aEl, bEl, t),
        distanceToLineM: distM,
      };
    }
  }
  return best;
};

export interface ElevationProfilePoint {
  distance_km: number;
  elevation_m: number;
}

export interface ElevationAndGrade {
  elevationM: number;
  gradePct: number | null;
}

/**
 * Высота + локальный уклон (%) на заданном расстоянии - интерполяция по уже
 * прореженным на бэкенде точкам профиля. Общая функция для графика высот и
 * тултипа курсора на карте, чтобы обе стороны показывали одно число.
 */
export const elevationAndGradeAtKm = (
  points: ElevationProfilePoint[],
  km: number
): ElevationAndGrade | null => {
  if (!points || points.length < 2) return null;
  let lo = 0;
  let hi = points.length - 1;
  while (hi - lo > 1) {
    const mid = (lo + hi) >> 1;
    if (points[mid].distance_km <= km) lo = mid;
    else hi = mid;
  }
  const a = points[lo];
  const b = points[hi];
  const segKm = b.distance_km - a.distance_km;
  const t = segKm === 0 ? 0 : Math.max(0, Math.min(1, (km - a.distance_km) / segKm));
  const elevationM = a.elevation_m + (b.elevation_m - a.elevation_m) * t;
  // Уклон ближайшего сегмента профиля - точки уже прорежены на бэкенде, поэтому это
  // средний уклон участка, а не шум отдельных GPS-точек.
  const gradePct = segKm > 0 ? ((b.elevation_m - a.elevation_m) / (segKm * 1000)) * 100 : null;
  return { elevationM, gradePct };
};

/** Интерполированная позиция на маршруте по накопленному расстоянию (ограничена длиной маршрута). */
export const pointAtDistance = (
  points: CursorRoutePoint[],
  targetKm: number,
  cumulativeKm?: number[]
): CursorPosition | null => {
  if (points.length < 2) return null;
  const cumKm = cumulativeKm ?? buildCumulativeKm(points);
  const total = cumKm[cumKm.length - 1];
  const km = Math.max(0, Math.min(targetKm, total));

  // cumKm монотонно неубывает - бинарным поиском находим нужный сегмент.
  let lo = 0;
  let hi = cumKm.length - 1;
  while (hi - lo > 1) {
    const mid = (lo + hi) >> 1;
    if (cumKm[mid] <= km) lo = mid;
    else hi = mid;
  }

  const segKm = cumKm[hi] - cumKm[lo];
  const t = segKm === 0 ? 0 : (km - cumKm[lo]) / segKm;
  const [aLat, aLng, aEl] = points[lo];
  const [bLat, bLng, bEl] = points[hi];
  return {
    lat: lerp(aLat, bLat, t),
    lng: lerp(aLng, bLng, t),
    elevationM: lerpElevation(aEl, bEl, t),
  };
};
